import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
    Database,
    Search as SearchIcon,
    RefreshCw,
    Brain,
    Layers,
    Lock,
    AlertTriangle,
    Cpu,
    Sparkles,
    Trash2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ClientPicker from "@/components/ClientPicker";

const KIND_META = {
    datalake_item: { label: "Datalake item", color: "text-emerald-300", bg: "bg-emerald-300/5", border: "border-emerald-300/30" },
    protocol: { label: "Protocol", color: "text-[#c7a6ff]", bg: "bg-[#7C3AED]/5", border: "border-[#7C3AED]/30" },
    common_knowledge: { label: "Common knowledge", color: "text-cyan-300", bg: "bg-cyan-300/5", border: "border-cyan-300/30" },
};

const CLASSIFICATION_META = {
    client_owned: { label: "Client owned", color: "text-emerald-300", border: "border-emerald-300/30" },
    multi_client_shared: { label: "Multi-client shared", color: "text-amber-300", border: "border-amber-300/30" },
    common_knowledge: { label: "Common knowledge", color: "text-cyan-300", border: "border-cyan-300/30" },
};

export default function RagPage() {
    const { user } = useAuth();
    const isSuperAdmin = (user?.role || "").toLowerCase() === "super_admin";

    const [stats, setStats] = useState(null);
    const [loadingStats, setLoadingStats] = useState(true);
    const [reindexing, setReindexing] = useState({});
    const [draining, setDraining] = useState(false);

    const [q, setQ] = useState("");
    const [kindFilter, setKindFilter] = useState("");
    const [classFilter, setClassFilter] = useState("");
    const [clientScope, setClientScope] = useState("");
    const [results, setResults] = useState(null);
    const [searching, setSearching] = useState(false);

    const loadStats = async () => {
        setLoadingStats(true);
        try {
            const { data } = await api.get("/admin/rag/status");
            setStats(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setLoadingStats(false);
        }
    };

    useEffect(() => { loadStats(); }, []);

    const reindex = async (kind, full = false) => {
        if (full && !window.confirm(`Full re-embed for ${kind}? This wipes existing embeddings and rebuilds them. Could take a while.`)) return;
        setReindexing((p) => ({ ...p, [kind]: true }));
        try {
            const { data } = await api.post("/admin/rag/reindex", { kinds: [kind], full });
            toast.success(`Enqueued ${data.enqueued?.[kind] || 0} ${kind} job(s)`);
            await loadStats();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setReindexing((p) => ({ ...p, [kind]: false }));
        }
    };

    const drainNow = async () => {
        setDraining(true);
        try {
            const { data } = await api.post("/admin/rag/tick");
            toast.success(`Drained ${data.drained || 0} job(s) — ${data.ok || 0} ok, ${data.failed || 0} failed`);
            await loadStats();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setDraining(false);
        }
    };

    const search = async (e) => {
        e?.preventDefault?.();
        if (!q.trim()) return;
        setSearching(true);
        setResults(null);
        try {
            const { data } = await api.post("/admin/rag/search", {
                q,
                kind: kindFilter || null,
                classification: classFilter || null,
                client_id_scope: clientScope || null,
                limit: 25,
            });
            setResults(data);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail));
        } finally {
            setSearching(false);
        }
    };

    return (
        <div className="px-8 py-8" data-testid="rag-page">
            <div className="flex items-start justify-between mb-8">
                <div>
                    <div className="label-xs text-[#7C3AED] flex items-center gap-2">
                        <Brain className="w-3 h-3" /> Phase Q · Tenant-Scoped RAG
                    </div>
                    <h1 className="font-display text-4xl tracking-tight mt-2">Vector Search</h1>
                    <p className="text-sm text-white/50 mt-2 max-w-2xl">
                        Semantic search across the embedded corpus — datalake items, protocols, and
                        common knowledge — with strict tenant isolation. Scoped admins only see vectors
                        their <code className="text-[#c7a6ff]">client_ids</code> own (plus multi-shared
                        and common-knowledge entries). Test-protocol embeddings live under the
                        <code className="text-[#c7a6ff] mx-1">_training</code> sentinel and never leak.
                    </p>
                </div>
                <button onClick={loadStats} disabled={loadingStats} data-testid="rag-refresh"
                    className="border border-white/15 px-3 py-2 rounded-md text-sm hover:bg-white/5 flex items-center gap-1.5 disabled:opacity-40">
                    <RefreshCw className={`w-4 h-4 ${loadingStats ? "animate-spin" : ""}`} /> Refresh
                </button>
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-6 gap-2 mb-5" data-testid="rag-kpis">
                <KpiCard label="Total embeddings" value={stats?.total ?? "—"} icon={Database} testId="rag-kpi-total" />
                <KpiCard label="Datalake" value={stats?.by_kind?.datalake_item ?? 0} icon={Layers} tone="emerald" testId="rag-kpi-datalake" />
                <KpiCard label="Protocol" value={stats?.by_kind?.protocol ?? 0} icon={Layers} tone="violet" testId="rag-kpi-protocol" />
                <KpiCard label="Common knowledge" value={stats?.by_kind?.common_knowledge ?? 0} icon={Layers} tone="cyan" testId="rag-kpi-common" />
                <KpiCard label="Queue pending" value={stats?.queue_pending ?? 0} icon={RefreshCw} tone={(stats?.queue_pending ?? 0) > 0 ? "amber" : undefined} testId="rag-kpi-pending" />
                <KpiCard label="Queue failed" value={stats?.queue_failed ?? 0} icon={AlertTriangle} tone={(stats?.queue_failed ?? 0) > 0 ? "red" : undefined} testId="rag-kpi-failed" />
            </div>

            {/* Embedder info */}
            {stats && (
                <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-3 mb-5 flex items-center gap-3 text-xs font-mono" data-testid="rag-embedder-info">
                    <Cpu className="w-3.5 h-3.5 text-[#7C3AED]" />
                    <span className="text-white/55">embedder:</span>
                    <span className="text-white">{stats.embedder}</span>
                    <span className="text-white/30">·</span>
                    <span className="text-white/55">dims:</span>
                    <span className="text-white">{stats.dims}</span>
                    {stats.last_indexed_at && (
                        <>
                            <span className="text-white/30">·</span>
                            <span className="text-white/55">last embedded:</span>
                            <span className="text-white">{new Date(stats.last_indexed_at).toLocaleString()}</span>
                        </>
                    )}
                    {stats.tenant_filter_applied && (
                        <span className="ml-auto flex items-center gap-1 text-amber-300">
                            <Lock className="w-3 h-3" /> tenant filter applied
                        </span>
                    )}
                </div>
            )}

            <div className="grid grid-cols-3 gap-5">
                {/* Left: semantic search test console */}
                <div className="col-span-2 bg-[#0a0a0a] border border-white/10 rounded-md p-5" data-testid="rag-search-panel">
                    <div className="flex items-center gap-2 mb-3">
                        <SearchIcon className="w-4 h-4 text-[#7C3AED]" />
                        <div className="font-display text-base">Semantic search console</div>
                    </div>
                    <form onSubmit={search} className="space-y-3">
                        <input
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Ask anything across your embedded corpus…"
                            data-testid="rag-search-q"
                            className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-[#7C3AED]/50"
                        />
                        <div className="grid grid-cols-3 gap-2">
                            <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} data-testid="rag-search-kind"
                                className="bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-xs font-mono">
                                <option value="">All kinds</option>
                                <option value="datalake_item">Datalake items</option>
                                <option value="protocol">Protocols</option>
                                <option value="common_knowledge">Common knowledge</option>
                            </select>
                            <select value={classFilter} onChange={(e) => setClassFilter(e.target.value)} data-testid="rag-search-class"
                                className="bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-xs font-mono">
                                <option value="">Any classification</option>
                                <option value="client_owned">Client owned</option>
                                <option value="multi_client_shared">Multi-client shared</option>
                                <option value="common_knowledge">Common knowledge</option>
                            </select>
                            {isSuperAdmin ? (
                                <ClientPicker
                                    value={clientScope}
                                    onChange={setClientScope}
                                    compact
                                    width="w-56"
                                    anyOptionLabel="Pin to client (any)"
                                    testId="rag-search-client"
                                />
                            ) : (
                                <div className="text-[10px] font-mono text-white/40 flex items-center px-3 border border-white/5 rounded-md bg-white/[0.02]">
                                    <Lock className="w-3 h-3 mr-1.5" /> client_id pin: super_admin only
                                </div>
                            )}
                        </div>
                        <button type="submit" disabled={searching || !q.trim()} data-testid="rag-search-submit"
                            className="bg-[#7C3AED] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#7C3AED]/90 flex items-center gap-2 disabled:opacity-40">
                            <Sparkles className="w-4 h-4" /> {searching ? "Searching…" : "Search"}
                        </button>
                    </form>

                    {/* Results */}
                    {results && (
                        <div className="mt-5" data-testid="rag-results">
                            <div className="text-[11px] font-mono text-white/45 mb-2">
                                {results.matched} match · {results.scanned} scanned · cosine top-{results.items?.length || 0}
                                {results.tenant_filter_applied && <span className="ml-2 text-amber-300">· tenant filter applied</span>}
                            </div>
                            {(results.items || []).length === 0 ? (
                                <div className="text-xs text-white/40 font-mono py-6 text-center" data-testid="rag-no-results">No vectors matched. Try a different phrase or reindex.</div>
                            ) : (
                                <div className="space-y-2">
                                    {results.items.map((it, i) => {
                                        const km = KIND_META[it.source_kind] || { label: it.source_kind, color: "text-white/70", bg: "bg-white/5", border: "border-white/15" };
                                        const cm = CLASSIFICATION_META[it.classification] || { label: it.classification, color: "text-white/60", border: "border-white/15" };
                                        return (
                                            <div key={it.id} className={`p-3 rounded-md border ${km.border} ${km.bg}`} data-testid={`rag-result-${i}`}>
                                                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                                                    <span className={`px-1.5 py-0.5 text-[10px] uppercase tracking-wider border ${km.border} ${km.color} rounded-sm font-mono`}>
                                                        {km.label}
                                                    </span>
                                                    <span className={`px-1.5 py-0.5 text-[10px] uppercase tracking-wider border ${cm.border} ${cm.color} rounded-sm font-mono`}>
                                                        {cm.label}
                                                    </span>
                                                    <span className="ml-auto text-[10px] font-mono text-white/45">
                                                        cosine {it.score?.toFixed?.(4)}
                                                    </span>
                                                </div>
                                                <div className="text-[12px] text-white/85 line-clamp-3">{it.text_excerpt}</div>
                                                <div className="text-[10px] font-mono text-white/40 mt-1.5 truncate">
                                                    owners: {(it.owners || []).join(", ") || "—"}
                                                    {it.data_region && <> · region: {it.data_region}</>}
                                                    {it.metadata?.protocol_number && <> · protocol: {it.metadata.protocol_number}</>}
                                                    {it.metadata?.kind && <> · kind: {it.metadata.kind}</>}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Right: reindex / queue panel */}
                <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-5 space-y-3" data-testid="rag-reindex-panel">
                    <div className="flex items-center gap-2">
                        <RefreshCw className="w-4 h-4 text-[#7C3AED]" />
                        <div className="font-display text-base">Indexing</div>
                    </div>
                    {!isSuperAdmin && (
                        <div className="p-2.5 rounded-md border border-amber-300/30 bg-amber-300/5 text-[11px] font-mono text-amber-200 flex items-start gap-2">
                            <Lock className="w-3 h-3 mt-0.5" />
                            Indexing actions are super_admin only. Status panel above is tenant-filtered.
                        </div>
                    )}
                    {["datalake_item", "protocol", "common_knowledge"].map((kind) => {
                        const meta = KIND_META[kind];
                        const count = stats?.by_kind?.[kind] ?? 0;
                        return (
                            <div key={kind} className={`p-3 rounded-md border ${meta.border} ${meta.bg}`} data-testid={`rag-reindex-row-${kind}`}>
                                <div className="flex items-center gap-2">
                                    <Layers className={`w-3.5 h-3.5 ${meta.color}`} />
                                    <div className="font-mono text-xs text-white">{meta.label}</div>
                                    <div className="ml-auto font-mono text-xs text-white/55">{count} embedded</div>
                                </div>
                                {isSuperAdmin && (
                                    <div className="flex gap-2 mt-2">
                                        <button onClick={() => reindex(kind, false)} disabled={reindexing[kind]} data-testid={`rag-reindex-${kind}`}
                                            className="px-2.5 py-1 text-[11px] border border-white/15 rounded hover:bg-white/5 disabled:opacity-40 flex items-center gap-1">
                                            {reindexing[kind] ? <RefreshCw className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                                            Re-embed new
                                        </button>
                                        <button onClick={() => reindex(kind, true)} disabled={reindexing[kind]} data-testid={`rag-reindex-full-${kind}`}
                                            className="px-2.5 py-1 text-[11px] border border-red-300/30 bg-red-300/5 text-red-200 rounded hover:bg-red-300/10 disabled:opacity-40 flex items-center gap-1">
                                            <Trash2 className="w-3 h-3" /> Wipe + rebuild
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                    {isSuperAdmin && (
                        <button onClick={drainNow} disabled={draining} data-testid="rag-drain-now"
                            className="w-full mt-3 px-3 py-2 border border-[#7C3AED]/40 bg-[#7C3AED]/10 text-[#c7a6ff] rounded-md text-xs flex items-center justify-center gap-2 hover:bg-[#7C3AED]/20 disabled:opacity-40">
                            <Sparkles className={`w-3.5 h-3.5 ${draining ? "animate-pulse" : ""}`} />
                            {draining ? "Draining…" : "Drain queue now"}
                        </button>
                    )}
                    <div className="text-[10px] font-mono text-white/40 leading-relaxed pt-2 border-t border-white/5">
                        Worker auto-drains every 10s and sweeps unembedded rows every minute. Manual
                        actions here are for batch backfills or after a prompt change.
                    </div>
                </div>
            </div>
        </div>
    );
}

function KpiCard({ label, value, icon: Icon, tone, testId }) {
    const toneClass = useMemo(() => {
        switch (tone) {
            case "violet": return "border-[#7C3AED]/30 bg-[#7C3AED]/5";
            case "emerald": return "border-emerald-300/30 bg-emerald-300/5";
            case "cyan": return "border-cyan-300/30 bg-cyan-300/5";
            case "amber": return "border-amber-300/30 bg-amber-300/5";
            case "red": return "border-red-300/30 bg-red-300/5";
            default: return "border-white/10 bg-[#0a0a0a]";
        }
    }, [tone]);
    return (
        <div className={`rounded-md border p-3 ${toneClass}`} data-testid={testId}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/55 font-mono">
                {Icon && <Icon className="w-3 h-3" />} {label}
            </div>
            <div className="font-display text-2xl mt-1">{value}</div>
        </div>
    );
}
